import { User } from "~/common/Store/models/User";
import { PricingGroupMember } from "~/ProductDetails/Pricing/PricingProduct";
import isActiveSale from "~/ProductDetails/Pricing/isActiveSale";
import shouldDisplayQuantityDiscounts from "~/ProductDetails/Pricing/shouldDisplayQuantityDiscounts";
import shouldDisplayRequiredCustomization from "~/common/Product/shouldDisplayRequiredCustomization";
import shouldDisplayBelowMapPrice from "~/ProductDetails/Pricing/shouldDisplayBelowMapPrice";
import getLowestPriceGroupMember from "~/ProductDetails/Pricing/getLowestPriceGroupMember";

export interface LowestPricingProduct {
  customizationRequired: boolean;
  customizationUrl: string;
  isCustomizable: boolean;
  minimumCustomizationPrice: number;
  showBelowMinimumAdvertisedPriceForLoggedInCustomers: boolean;
  showBelowMinimumAdvertisedPriceForPlatinumAndPlusCustomers: boolean;
  startBuy: number;
  groupingAttributes: {
    isVirtual: boolean;
    isGroupMaster: boolean;
    groupMembers: PricingGroupMember[];
  };
  price: {
    minimumAdvertisedPriceProperties: {
      price: number;
      type: string;
    };
    minimumAdvertisedPriceOverride: {
      priceLevel5: number;
    };
    quantityDiscounts: {
      endQuantity?: number;
      isLotDiscount: boolean;
      quantity: number;
      price: number;
      startQuantity: number;
    }[];
    displaySalePrice: boolean;
    saleStartDate?: string;
    saleEndDate?: string;
    salePrice: number;
    price: number;
    displayMinimumAdvertisedPrice: boolean;
  };
}

export const getLowestDisplayPrice = (
  product: LowestPricingProduct,
  user: User,
  excludeQuantityDiscounts = false,
  excludePlusPrice = false,
): number => {
  const { price, minimumCustomizationPrice = 0 } = product;

  if (shouldDisplayRequiredCustomization(product)) {
    return minimumCustomizationPrice;
  }

  // p1
  const prices = [price.price];

  // sale price
  if (price.salePrice && isActiveSale(price)) {
    prices.push(price.salePrice);
  }

  // vg master price
  if (
    product.groupingAttributes.isGroupMaster &&
    product.groupingAttributes.groupMembers.length > 1
  ) {
    const lowerPriceGroupMember = getLowestPriceGroupMember(
      product.groupingAttributes.groupMembers,
      user,
    );
    prices.push(lowerPriceGroupMember.price.price);
  }

  // p4
  if (
    !!price.minimumAdvertisedPriceProperties?.type?.trim() &&
    price.minimumAdvertisedPriceProperties.price &&
    (price.minimumAdvertisedPriceProperties.type.trim() === "G" ||
      shouldDisplayBelowMapPrice(product, user))
  ) {
    prices.push(price.minimumAdvertisedPriceProperties.price);
  }

  // p5
  if (
    !excludePlusPrice &&
    !!price.minimumAdvertisedPriceOverride?.priceLevel5 &&
    user.hasWebPlus
  ) {
    prices.push(price.minimumAdvertisedPriceOverride.priceLevel5);
  }

  if (excludeQuantityDiscounts || !price.quantityDiscounts?.length) {
    return Math.min(...prices);
  }

  // quantity discounts
  const [displayQuantityDiscounts, quantityDiscounts] =
    shouldDisplayQuantityDiscounts(product, user);

  if (displayQuantityDiscounts) {
    const cheapestQuantityDiscount = displayQuantityDiscounts
      ? quantityDiscounts[quantityDiscounts.length - 1]?.price
      : undefined;

    prices.push(cheapestQuantityDiscount || Infinity);
  }

  return Math.min(...prices);
};

export default getLowestDisplayPrice;
